import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import {
    Card,
    CardBody,
    Badge,
    Input,
    Row,
    Col
} from "reactstrap";
import { getSubscribers } from "../../../../../api-handle/subscriptionApi";
import { useDebounce } from './../../../hooks/debounce';

const formatDateDDMMYYYY = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "N/A";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
};

const SubscriptionTable = () => {
    const [data, setData] = useState({ subscribers: [], total: 0 });
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [sortField, setSortField] = useState("createdAt");
    const [sortOrder, setSortOrder] = useState("desc");
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const debouncedSearch = useDebounce(search, 300)


    useEffect(() => {
        fetchSubscribers();
    }, [debouncedSearch, statusFilter, sortField, sortOrder, page, perPage]);

    const fetchSubscribers = async () => {
        try {
            const response = await getSubscribers({
                search: debouncedSearch,
                status: statusFilter,
                sortField,
                sortOrder,
                size: perPage,
                page,
            });

            if (response?.isSuccess) {
                setData({
                    subscribers: response.data,
                    total: response.total,
                });
            }
        } catch (error) {
            console.error("Error fetching subscribers:", error);
        }
    };

    const handleSort = (column, sortDirection) => {
        setSortField(column.selector);
        setSortOrder(sortDirection);
    };

    const columns = [
        {
            name: "Plan",
            selector: (row) => row.planName || "N/A",
            sortable: true
        },
        {
            name: "User Name",
            selector: (row) => row.username || "N/A",
            sortable: true
        },
        {
            name: "Email",
            selector: (row) => row.email || "N/A",
            sortable: true
        },
        {
            name: "Start Date",
            selector: (row) => formatDateDDMMYYYY(row.startDate),
            sortable: true
        },
        {
            name: "End Date",
            selector: (row) => formatDateDDMMYYYY(row.endDate),
            sortable: true
        },
        {
            name: "Status",
            selector: (row) => row.status || "N/A",
            cell: (row) => {
                const status = row.status?.toLowerCase() || "N/A";
                return (
                    <Badge color={status === "active" ? "success" : "danger"}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Badge>
                );
            },
            sortable: true
        }
    ];


    return (
        <>
            {/* 🔍 Filters */}
            <Card className="mb-3">
                <CardBody>
                    <h4 className="fw-bold mb-3">Subscribers List</h4>
                    <Row>
                        <Col md="6" sm="12" className="mb-2">
                            <Input
                                type="text"
                                placeholder="Search by Name or Email..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </Col>
                        <Col md="6" sm="12">
                            <Input type="select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="">All Status</option>
                                <option value="active">Active</option>
                                <option value="expired">Expired</option>
                                <option value="cancelled">Cancelled</option>
                            </Input>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* 📋 Single Table */}
            <Card>
                <CardBody>
                    <DataTable
                        columns={columns}
                        data={data.subscribers}
                        pagination
                        sortServer
                        onSort={handleSort}
                        paginationServer
                        paginationTotalRows={data.total}
                        paginationPerPage={perPage}
                        paginationDefaultPage={page}
                        paginationRowsPerPageOptions={[5, 10, 20]}
                        onChangePage={(newPage) => setPage(newPage)}
                        onChangeRowsPerPage={(newPerPage) => {
                            setPerPage(newPerPage);
                            setPage(1);
                        }}
                        highlightOnHover
                        striped
                    />
                </CardBody>
            </Card>
        </>
    );
};

export default SubscriptionTable;